import {
  Button,
  Dropdown,
  MenuProps,
  notification,
  Pagination,
  PaginationProps,
  Spin,
  Tabs,
  TabsProps,
  Tooltip,
} from 'antd';
import classNames from 'classnames';
import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import {
  Grid,
  GridLabels,
  GridRow,
} from '../../../../../../common/components/Grid/Grid';
import styles from './MyLeadsContactsTable.module.css';
import { CONTACTS_TABLE_CELL_DATA } from './render-contacts-table-cells';
import { useResolutionInfo } from '@mm/hooks';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import {
  activeChatbotIdSelector,
  connectedPageSelector,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { ContactSlideoutMenuProps } from '../../Audiences/ContactSlideoutMenu/ContactSlideoutMenu';
import { Contact, SelectedContact } from '@mm/state/interfaces/contacts';
import { ContactDeleteModal } from '../../../../contacts/components/ContactDeleteModal/ContactDeleteModal';

import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useCreateMyLeadsAudience } from '../hooks/use-create-my-leads-audience';
import { SelectAllLeadsButton } from '../SelectAllLeadsButton/SelectAllLeadsButton';
import { MOBILE_SMALL } from '../../../../../constants/screenSize';
import {
  MyLeadsCreateAudienceRequestParams,
  MyLeadsFilterTypes,
  MyLeadsSearchQuery,
  SubsectionKeys,
} from '@mm/state/interfaces/myLeads';
import { useMyLeadsCRUDActions } from '@mm/state/actions/MyLeadsCRUDActions';
import { selectContacts } from '@mm/state/selectors/contactsSelector';
import { useFilterParams } from '../MyLeadsFiltersPanel/use-filter-params';
import { useDummyContacts } from '../hooks/use-dummy-contacts';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { useHistory } from 'react-router-dom';
import { ReactComponent as Arrow } from './arrow.svg';
import { selectPromotersByType } from '@mm/state/selectors/promoterSelector';
import { randomId } from '../../../../../../common/helpers/utils';
import { ReactComponent as EmptyIcon } from 'assets/empty-icon.svg';
import pluralize from 'pluralize';
import { UserRoleContainer } from '../../../../../../common/hooks/use-route-guard';
import { TagsFilters } from '@mm/state/model/AudiencesModel';
import { TableLabel } from '../../../../../../common/components/TableGrid/TableGrid';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import {
  CaretDownOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { ReactComponent as AudiencesIcon } from 'assets/audiences-icon.svg';
import { ConditionsIcon } from '@mm/icons';

type Props = {
  filterParams: MyLeadsSearchQuery;
  setFilterParams: (params: MyLeadsSearchQuery) => void;
  isFetchingContacts?: boolean;
  processClick: (callbackFn: (args: any) => void) => void;
  setTotalContacts: (count: number) => void;
  saveAudienceButtonRef?: MutableRefObject<HTMLButtonElement | null>;
  leadRef?: MutableRefObject<HTMLDivElement | null>;
  toggleSortOrder: () => void;
  canUseSorting: boolean;
  sortOrder: 'asc' | 'desc';
  activeTab?: string;
  setActiveTab?: (activeTab: string | undefined) => void;
};

// tslint:disable-next-line: cyclomatic-complexity
export const MyLeadsContactsTable = ({
  filterParams,
  setFilterParams,
  isFetchingContacts,
  processClick,
  setTotalContacts,
  saveAudienceButtonRef,
  leadRef,
  toggleSortOrder,
  canUseSorting,
  sortOrder,
  setActiveTab,
  activeTab,
}: Props) => {
  const connectedPage = useSelector(connectedPageSelector);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const { pagination, contacts, allContactsCount } =
    useSelector(selectContacts);
  const dummyContacts = useDummyContacts();
  const getemailsPromoters = useSelector(state =>
    selectPromotersByType(state, 'getemails'),
  );
  const hasActivePromoter = getemailsPromoters.find(p => p.active);

  const { total: totalContacts, perPage, page } = pagination || {};

  const hasLeads = totalContacts > 0;

  const [selectedCheckboxes, setSelectedCheckboxes] = useState<number[]>([]);
  const [excludedLeadIds, setExcludedLeadIds] = useState<number[]>([]);
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedLeadsCount, setSelectedLeadsCount] = useState<
    number | undefined
  >(undefined);
  const noSelectedLeads = !selectedCheckboxes.length && !selectedLeadsCount;
  const [contactForDeletion, setContactForDeletion] = useState<Contact>();

  const [tabCounters, setTabCounters] = useState<Record<string, number | null>>(
    {
      new: null,
      existing: null,
      all: null,
    },
  );
  const [busy, setBusy] = useState(isFetchingContacts || false);
  const isFetching = busy || isFetchingContacts;
  const {
    hasFilters,
    getFilterParamsForActiveTab,
    hasFiltersWithoutExceptionKeys,
  } = useFilterParams({
    filterParams,
  });

  const {
    roleFlags: { isMonitorLeadsRole },
  } = UserRoleContainer.useContainer();

  const contactsCRUDActions = useContactsCRUDActions();
  const { searchLeads, getFilteredContactsCount } = useMyLeadsCRUDActions();
  const { showSlideoutMenu, hideSlideoutMenu } = useViewActions();
  const { openCreateMyLeadsAudienceModal, openDeleteConfirmModal } =
    useModalActions();
  const history = useHistory();
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_SMALL;
  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  const showEmptyTableState = !hasLeads && hasFilters && !isFetching;
  const rendredContacts = showEmptyTableState
    ? []
    : contacts.length
    ? contacts
    : dummyContacts;

  const onSelectLeads = (isAllSelected: boolean, leadsCount: number) => {
    setSelectedAll(isAllSelected);
    setSelectedCheckboxes([]);
    setExcludedLeadIds([]);
    setSelectedLeadsCount(leadsCount);
  };

  const { createMyLeadsAudience } = useCreateMyLeadsAudience(() =>
    onSelectLeads(false, 0),
  );

  const GRID_TEMPLATE_COLUMNS = isMobile ? '1fr' : '1.25fr 1.75fr 0.25fr';

  const invalidSelectedLeadsCount =
    !selectedLeadsCount || selectedLeadsCount - excludedLeadIds.length === 0;
  const disabledActions =
    !hasLeads || noSelectedLeads || invalidSelectedLeadsCount;

  const isLimitedQtyAudience = selectedLeadsCount && !selectedCheckboxes.length;

  const isCheckboxChecked = (contactId: number, idx: number) => {
    const index = (page - 1) * perPage + idx;
    if (selectedAll || isLimitedQtyAudience) {
      return (
        !excludedLeadIds.some(checkboxId => checkboxId === contactId) &&
        index < Number(selectedLeadsCount)
      );
    }
    return selectedCheckboxes.some(checkboxId => checkboxId === contactId);
  };

  const isCheckboxDisabled = (idx: number) => {
    const index = (page - 1) * perPage + idx;
    if (selectedAll || isLimitedQtyAudience) {
      return index >= (selectedLeadsCount || 0);
    }
  };

  const onCheckboxClick = (contactId: number, idx: number) => {
    const isExisting = isCheckboxChecked(contactId, idx);

    if (selectedAll || isLimitedQtyAudience) {
      if (isExisting) {
        setExcludedLeadIds(current => [...current, contactId]);
      } else {
        setExcludedLeadIds(current => [
          ...current.filter(checkboxId => checkboxId !== contactId),
        ]);
      }
      return;
    }

    if (isExisting) {
      setSelectedCheckboxes(current => [
        ...current.filter(checkboxId => checkboxId !== contactId),
      ]);
      setSelectedLeadsCount(prev => ((prev || 0) > 0 ? (prev || 0) - 1 : 0));
    } else {
      setSelectedCheckboxes(current => [...current, contactId]);
      setSelectedLeadsCount(prev => (prev || 0) + 1);
    }
  };

  const getUniqueIds = (values: number[]) => [...new Set(values)];

  const onCreateAudienceClick = async ({
    withAddToAutomation,
    withExport,
  }: {
    withAddToAutomation?: boolean;
    withExport?: boolean;
  }) => {
    const uniqCheckboxes = getUniqueIds([...selectedCheckboxes]);

    const params = (
      uniqCheckboxes.length
        ? {
            leadIds: uniqCheckboxes,
          }
        : {
            ...filterParams,
            esFilters: filterParams.query,
            ...(!selectedAll && { limit: selectedLeadsCount }),
            ...(excludedLeadIds.length && { excludedLeadIds }),
          }
    ) as MyLeadsCreateAudienceRequestParams;

    if (withAddToAutomation || withExport) {
      createMyLeadsAudience({
        params,
        withAddToAutomation,
        withExport,
        isSelectedAll: selectedAll && selectedLeadsCount === allContactsCount,
      });
      return;
    }

    openCreateMyLeadsAudienceModal({
      onSave: audienceName =>
        createMyLeadsAudience({
          params: {
            ...params,
            name: audienceName,
          },
        }),
    });
  };

  const onPaginationChange: PaginationProps['onChange'] = async page => {
    const updatedParams = {
      ...filterParams,
      page,
    };

    setBusy(true);
    setFilterParams(updatedParams);
    setBusy(false);
  };

  const deleteSelectedContact = async (
    contactId: number,
    eraseRemoteMessages,
  ) => {
    if (connectedPage) {
      await contactsCRUDActions.deleteSelectedContact(
        connectedPage.id,
        contactId,
        pagination?.page,
        pagination?.perPage,
        undefined,
        eraseRemoteMessages,
      );

      await searchLeads(filterParams, true);
      hideSlideoutMenu();
    }
  };

  const clear = () => {
    setSelectedCheckboxes([]);
    setSelectedLeadsCount(undefined);
    setSelectedAll(false);
  };

  const filterContactsToDeleteCallback = (count: number, idx: number) =>
    count < 25 ? idx < count : idx < 25;

  const pollContacts = (deletedContactsCount: number) => {
    let pollingAttempts = 0;
    const pollInterval = 5000;
    const maxAttempts = 3;

    const pollRequest = async () => {
      try {
        if (connectedPage) {
          await contactsCRUDActions.retrieveAll(
            connectedPage.id,
            filterParams.page || 1,
            undefined,
            undefined,
            {
              audience: {
                esFilters: filterParams.query,
                tagsFilters: filterParams.tagsFilters as TagsFilters,
                filterParams: filterParams.filterParams,
              },
              withDeliverable: true,
              skipBusy: true,
              ...(canUseSorting && sortOrder
                ? {
                    sortColumn: 'created_at',
                    sortDirection: sortOrder,
                  }
                : {}),
            },
          );
          pollingAttempts += 1;
          setTotalContacts((allContactsCount || 0) - deletedContactsCount);
          if (pollingAttempts < maxAttempts) {
            setTimeout(pollRequest, pollInterval);
          } else {
            return;
          }
        }
      } catch (error) {
        throw new Error(`Request error: ${error}`);
      }
    };

    setTimeout(pollRequest, 1000);
  };

  const contactsBatchDelete = () => {
    const contactIdsToDelete = selectedCheckboxes.length
      ? selectedCheckboxes.filter((_, idx) =>
          filterContactsToDeleteCallback(selectedCheckboxes.length, idx),
        )
      : selectedLeadsCount
      ? contacts
          .filter((_, idx) =>
            filterContactsToDeleteCallback(selectedLeadsCount, idx),
          )
          .map(c => c.id)
      : [];
    const contactsCount = contactIdsToDelete.length;
    openDeleteConfirmModal({
      headerText: 'Deleting contacts will remove all data forever.',
      deleteButtonName: 'Delete Contacts',
      description: `Contacts that are managed in ${companyName} are enriched with return visit data, 
      updated consumer attributes monthly, and can be used in our outreach automation builder.`,
      deleteCallback: async () => {
        if (connectedPage && contactIdsToDelete.length) {
          await contactsCRUDActions.contactsBatchDelete(
            connectedPage.id,
            contactIdsToDelete,
          );
          pollContacts(contactIdsToDelete.length);
          notification.success({
            message: `${contactsCount} ${pluralize('contact', contactsCount)} ${
              contactsCount === 1 ? 'was' : 'were'
            } deleted`,
          });
          clear();
        }
      },
      newStyle: true,
    });
  };

  const getSelectedContact = (contact?: Contact) => {
    const selectedContact = contact?.dummy
      ? (dummyContacts.find(c => c.id === contact.id) as any as SelectedContact)
      : undefined;
    openContactSlideoutMenu({
      contactId: contact?.id,
      selectedContact,
      deleteSelectedContact,
      onContactSelect: () => getSelectedContact(contact),
      onContactUpdateSuccess: async () => {
        await searchLeads(filterParams, true);
      },
    });
  };

  const openContactSlideoutMenu = useCallback(
    (props: ContactSlideoutMenuProps) => {
      showSlideoutMenu('contact', props);
    },
    [],
  );

  const navigateToXRay = () => {
    history.push(BotBuilderRoutes.xRay.getLink({ chatbotId }));
  };

  const onResetAll = () => {
    setFilterParams({ query: {} });
  };

  const isShopify = useSelector(selectIsShopifyContext);

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: 'Build an Audience',
      icon: (
        <AudiencesIcon
          className={classNames({
            [styles.disabledIcon]: disabledActions,
          })}
        />
      ),
      onClick: () => processClick(() => onCreateAudienceClick({})),
      disabled: disabledActions,
    },
    ...(!isShopify && !isMonitorLeadsRole
      ? [
          {
            key: '2',
            label: 'Add to Automation',
            icon: (
              <ConditionsIcon
                color={disabledActions ? 'var(--color-gray-6)' : '#262626'}
                size={18}
              />
            ),
            onClick: () =>
              processClick(() =>
                onCreateAudienceClick({ withAddToAutomation: true }),
              ),
            disabled: disabledActions,
          },
        ]
      : []),
    {
      key: '3',
      label: 'Export',
      icon: <DownloadOutlined style={{ fontSize: 18 }} />,
      onClick: () =>
        processClick(() => onCreateAudienceClick({ withExport: true })),
      disabled: disabledActions,
    },
    {
      key: '4',
      label: 'Delete',
      icon: <DeleteOutlined style={{ fontSize: 18 }} />,
      onClick: contactsBatchDelete,
      disabled: disabledActions,
      danger: true,
    },
  ];

  const tabItems: TabsProps['items'] = [
    {
      key: 'new',
      label: (
        <div>New Contacts ({tabCounters.new?.toLocaleString() || 0}) </div>
      ),
    },
    {
      key: 'existing',
      label: (
        <div>
          Existing Contacts ({tabCounters.existing?.toLocaleString() || 0}){' '}
        </div>
      ),
    },
    {
      key: 'all',
      label: (
        <div>All Contacts ({tabCounters.all?.toLocaleString() || 0}) </div>
      ),
    },
  ];

  const fetchContactsCounters = async (key: string) => {
    const result = await getFilteredContactsCount(
      getFilterParamsForActiveTab(key),
    );
    setTabCounters(prev => ({ ...prev, [key]: result }));
  };

  useEffect(() => {
    if (!activeTab) {
      setActiveTab?.('new');
    }
  }, [activeTab]);

  useEffect(() => {
    fetchContactsCounters('new');
    fetchContactsCounters('existing');
    fetchContactsCounters('all');
  }, []);

  useEffect(() => {
    clear();
  }, [
    filterParams.query?.attributes,
    filterParams.query?.variables,
    filterParams.exclude,
  ]);

  return (
    <div className={styles.container}>
      <div className={styles.bar}>
        {Boolean(totalContacts) && (
          <div className={styles.leadsCount}>
            {totalContacts.toLocaleString('en-US')} filtered
          </div>
        )}
        <div className={styles.buttonsArea}>
          <SelectAllLeadsButton
            disabled={!hasLeads}
            hideSelectAll={!hasFilters}
            maxLeadsCount={totalContacts}
            count={Number(selectedLeadsCount) - excludedLeadIds.length}
            setCount={setSelectedLeadsCount}
            onSelectLeads={onSelectLeads}
          />

          <Dropdown menu={{ items }}>
            <Button
              ref={saveAudienceButtonRef}
              type="primary"
              disabled={disabledActions}
              className={styles.primaryButton}
            >
              Actions
              <CaretDownOutlined />
            </Button>
          </Dropdown>
        </div>
      </div>

      {!hasFiltersWithoutExceptionKeys && (
        <Tabs
          className={styles.tabs}
          activeKey={activeTab}
          items={tabItems}
          onChange={key => setActiveTab?.(key)}
        />
      )}

      <div className={styles.gridWrapper}>
        <Grid className={styles.grid}>
          <GridLabels
            className={classNames(styles.gridLabels, {
              [styles.gridLabelsEmptyState]: !hasLeads && hasFilters,
            })}
            gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
          >
            {CONTACTS_TABLE_CELL_DATA.map(item => {
              const isFirstNameColumns = item.key === 'firstName';
              return (
                <TableLabel
                  key={item.label?.toString()}
                  dataItem={{
                    ...item,
                    keyName: item.key,
                    onChange:
                      item.key === 'createdAt' && canUseSorting
                        ? toggleSortOrder
                        : undefined,
                    className: classNames(styles.tableHeader, {
                      [styles.firstNameHeading]: isFirstNameColumns,
                    }),
                  }}
                  orderByAtTheEnd
                />
              );
            })}
          </GridLabels>

          <div
            className={classNames(styles.gridRows, {
              [styles.gridRowsDisabled]: isFetching,
            })}
            ref={leadRef}
          >
            <Spin
              tip="Retrieving contacts..."
              spinning={isFetching}
              className={styles.spin}
            >
              {!isFetching &&
                rendredContacts?.map((contact, idx) => {
                  const isChecked = isCheckboxChecked(contact.id, idx);
                  const isDisabled = isCheckboxDisabled(idx);
                  return (
                    <GridRow
                      key={contact.id.toString()}
                      className={classNames(styles.gridRow, {
                        [styles.gridRowSelected]: isChecked,
                        [styles.disabledGridRow]: isDisabled,
                      })}
                      gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
                      onClick={() => getSelectedContact(contact)}
                    >
                      {CONTACTS_TABLE_CELL_DATA.map(
                        ({ Cell, key: keyName }) => (
                          <Tooltip
                            key={randomId()}
                            title={
                              contact.dummy &&
                              'Your contacts will appear like this'
                            }
                          >
                            {Cell({
                              contact,
                              key: keyName,
                              onClick: e => {
                                if (keyName === 'fullName') {
                                  e?.stopPropagation();
                                  onCheckboxClick(contact.id, idx);
                                }
                                if (keyName === 'action') {
                                  setContactForDeletion(contact);
                                }
                              },
                              isChecked,
                              disabled: isDisabled || !!isLimitedQtyAudience,
                            })}
                          </Tooltip>
                        ),
                      )}
                    </GridRow>
                  );
                })}
            </Spin>
          </div>
        </Grid>
        <ContactDeleteModal
          showEraseRemoteMessagesOption={
            !!contactForDeletion?.remoteId.startsWith('em_')
          }
          show={!!contactForDeletion}
          emailAddress={contactForDeletion?.email || ''}
          onDelete={async eraseRemoteMessages => {
            if (contactForDeletion) {
              await deleteSelectedContact(
                contactForDeletion.id,
                eraseRemoteMessages,
              );
            }
          }}
          onClose={() => setContactForDeletion(undefined)}
        />
      </div>
      {!!hasLeads && (
        <div className={styles.paginationWrapper}>
          <Pagination
            current={page}
            onChange={onPaginationChange}
            pageSize={perPage}
            showSizeChanger={false}
            total={totalContacts}
            disabled={isFetching}
            hideOnSinglePage
          />
        </div>
      )}
      {showEmptyTableState && (
        <div className={styles.noResultsBlock}>
          <div className={styles.noResultsTitle}>
            No contacts match your current filters
          </div>
          <div>
            Edit current filters or{' '}
            <span className={styles.noResultsLink} onClick={onResetAll}>
              reset all filters
            </span>
          </div>
          <EmptyIcon className={styles.noResultsIcon} />
        </div>
      )}

      {!hasLeads && !hasFilters && dummyContacts?.length && !isFetching && (
        <>
          <div className={styles.emptyStateBlock}>
            <Arrow />
            <div className={styles.emptyLabel}>
              Your contacts will appear like this
            </div>
          </div>
          {(!connectedPage?.getemailsActivated ||
            (connectedPage?.getemailsActivated &&
              !hasActivePromoter &&
              getemailsPromoters.length)) && (
            <PrimaryButton
              className={classNames(
                styles.primaryButton,
                styles.emptyStateButton,
              )}
              onClick={navigateToXRay}
            >
              {!connectedPage?.getemailsActivated
                ? 'Install X-Ray Pixel'
                : 'Turn X-Ray Trigger on'}
            </PrimaryButton>
          )}
        </>
      )}
    </div>
  );
};
